/**
 * Image reference resolution for scraped question-bank problems.
 *
 * `images` entries are not shape-guaranteed: S3 URLs, bare bucket keys,
 * `web+graphie://` refs, `{url,alt}`, `{grid,html}`, `"[object Object]"`, and
 * sometimes the whole array as a JSON string. See the tests for each shape.
 *
 * Mirror-only: never emit a cdn.kastatic.org URL. Unmirrored assets surface as
 * an explicit chip so the gap stays visible.
 */

export const S3_MEDIA_BASE =
  "https://question-scrapper-image-bucket.s3.us-west-1.amazonaws.com";

export type ImageRef =
  | { kind: "image"; candidates: string[]; alt: string; raw: string; diagram?: true }
  | { kind: "html"; html: string; raw: string }
  | { kind: "spec"; specType: string; summary: string; raw: string }
  | { kind: "diagram"; raw: string }
  | { kind: "unresolved"; raw: string; reason: string };

/** Graphie refs are extension-less and served as .svg. */
const GRAPHIE_RE =
  /^(?:web\+graphie|https?):\/\/cdn\.kastatic\.org\/ka-perseus-graphie\/([A-Za-z0-9_-]+?)(?:\.(?:svg|png|jpe?g|gif|webp))?(?:[?#].*)?$/i;

/** Khan's other image path — carries a real extension, so keep it. */
const KA_IMAGES_RE =
  /^(?:web\+graphie|https?):\/\/cdn\.kastatic\.org\/ka-perseus-images\/([A-Za-z0-9_-]+?)(?:\.(svg|png|jpe?g|gif|webp))?(?:[?#].*)?$/i;

/** Any Khan-hosted URL, whatever the path. */
const KA_HOST_RE = /^(?:web\+graphie|https?):\/\/(?:[a-z0-9-]+\.)*kastatic\.org\//i;

/** Bare bucket key: `ka/<hash>.svg`, `ka/<hash>`, `thinkverse/<id>.png`, ... */
const BUCKET_KEY_RE =
  /^(ka|thinkverse|ixl)\/([^/?#]+?)(?:\.(svg|png|jpe?g|gif|webp))?(?:[?#].*)?$/i;

/** Extension guesses for keys stored without one, most likely first. */
const EXT_GUESSES: Record<string, string[]> = {
  ka: ["svg", "png"],
  thinkverse: ["png", "svg", "jpg"],
  ixl: ["png", "svg", "jpg"],
};

const DEAD_STRINGS = new Set([
  "[object object]",
  "null",
  "undefined",
  "none",
  "nan",
]);

const MAX_RAW = 160;

function truncate(s: string): string {
  return s.length > MAX_RAW ? `${s.slice(0, MAX_RAW)}…` : s;
}

function uniq(list: (string | undefined)[]): string[] {
  const out: string[] = [];
  for (const v of list) if (v && !out.includes(v)) out.push(v);
  return out;
}

/**
 * Ordered URLs to try for one string reference; [0] is most likely to 200.
 * Empty array means the reference cannot be resolved to our bucket.
 */
export function imageCandidates(src: string): string[] {
  const raw = src.trim();
  if (!raw || DEAD_STRINGS.has(raw.toLowerCase())) return [];

  const graphie = GRAPHIE_RE.exec(raw);
  if (graphie) {
    const hash = graphie[1];
    if (!hash) return [];
    // Bucket keys are case-sensitive and the scraper wrote lowercase sha1s.
    return [`${S3_MEDIA_BASE}/ka/${hash.toLowerCase()}.svg`];
  }

  const kaImage = KA_IMAGES_RE.exec(raw);
  if (kaImage) {
    const hash = kaImage[1];
    if (!hash) return [];
    const ext = (kaImage[2] ?? "png").toLowerCase();
    return [`${S3_MEDIA_BASE}/ka/${hash.toLowerCase()}.${ext}`];
  }

  const key = BUCKET_KEY_RE.exec(raw);
  if (key) {
    const source = (key[1] ?? "").toLowerCase();
    const id = key[2] ?? "";
    const ext = key[3]?.toLowerCase();
    if (!id) return [];
    const exts = ext ? [ext] : (EXT_GUESSES[source] ?? ["png"]);
    return uniq(exts.map((e) => `${S3_MEDIA_BASE}/${source}/${id}.${e}`));
  }

  if (/^data:image\//i.test(raw)) return [raw];

  // Mirror-only has to be enforced, not just implied by the two path regexes
  // above: a Khan URL under some other path would otherwise fall into the
  // generic passthrough and hotlink their CDN. We have no mirror key for an
  // unrecognised path, so report it instead.
  if (KA_HOST_RE.test(raw)) return [];

  // Other absolute URLs already in the data pass through; stripping them would
  // regress figures that render today.
  if (/^https?:\/\//i.test(raw)) return [raw];
  if (raw.startsWith("//")) return [`https:${raw}`];

  return [];
}

// ── Thinkverse figure specs ──
// Declarative "plot this function" objects with no URL, so nothing to mirror.
// Summarised until something renders them.

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function round(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, "");
}

/** `bound` is [xMin, yMax, xMax, yMin]. */
function describeBound(bound: unknown): string {
  if (!Array.isArray(bound) || bound.length < 4) return "";
  const xMin = num(bound[0]);
  const yMax = num(bound[1]);
  const xMax = num(bound[2]);
  const yMin = num(bound[3]);
  if (xMin === null || yMax === null || xMax === null || yMin === null) return "";
  return `x ∈ [${round(xMin)}, ${round(xMax)}], y ∈ [${round(yMin)}, ${round(yMax)}]`;
}

/** Make a Thinkverse LaTeX-ish function string readable. */
function readableFunc(f: string): string {
  return f
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot/g, "·")
    .replace(/\\frac(\d)(\d)/g, "$1/$2")
    .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, "($1)/($2)")
    .replace(/\\/g, "")
    .trim();
}

function describeSpec(o: Record<string, unknown>): { type: string; summary: string } | null {
  const type = typeof o.type === "string" ? o.type : "";
  if (!type) return null;

  const bound = describeBound(o.bound);
  const parts: string[] = [];

  if (type === "graph") {
    const lines = Array.isArray(o.lines) ? o.lines : [];
    const funcs = lines
      .map((l) =>
        l && typeof l === "object" && typeof (l as Record<string, unknown>).func === "string"
          ? readableFunc((l as Record<string, unknown>).func as string)
          : null,
      )
      .filter(Boolean) as string[];
    const points = Array.isArray(o.points) ? o.points : [];
    if (funcs.length) parts.push(funcs.join(", "));
    if (points.length) {
      const labels = points
        .map((p) => {
          if (!p || typeof p !== "object") return null;
          const pt = p as Record<string, unknown>;
          if (typeof pt.label === "string" && pt.label) return pt.label;
          const x = num(pt.x);
          const y = num(pt.y);
          return x !== null && y !== null ? `(${round(x)}, ${round(y)})` : null;
        })
        .filter(Boolean) as string[];
      if (labels.length) parts.push(`points ${labels.slice(0, 6).join(", ")}`);
    }
    if (!parts.length) parts.push("empty plot");
  } else if (type === "geometry") {
    const shapes = Array.isArray(o.value) ? o.value : [];
    const desc = shapes
      .map((s) => {
        if (!s || typeof s !== "object") return null;
        const sh = s as Record<string, unknown>;
        const kind = typeof sh.type === "string" ? sh.type : "shape";
        const n = Array.isArray(sh.points) ? sh.points.length : 0;
        return n ? `${kind} (${n} points)` : kind;
      })
      .filter(Boolean) as string[];
    parts.push(desc.length ? desc.join(", ") : "no shapes");
  } else if (type === "range") {
    const start = num(o.start);
    const step = num(o.step);
    const total = num(o.total);
    if (start !== null && step !== null && total !== null) {
      parts.push(`from ${round(start)}, step ${round(step)}, ${round(total)} ticks`);
    }
    if (Array.isArray(o.value)) {
      const marks = o.value.filter((v) => typeof v === "number").map((v) => round(v as number));
      if (marks.length) parts.push(`marked ${marks.join(", ")}`);
    }
  } else if (type === "fractionGrids") {
    const w = num(o.width);
    const h = num(o.height);
    if (w !== null && h !== null) parts.push(`${round(w)} x ${round(h)} grid`);
    if (Array.isArray(o.value)) parts.push(`${o.value.length} cells set`);
  } else {
    // Unknown spec type — still better than dumping JSON.
    parts.push("figure spec");
  }

  if (bound) parts.push(bound);
  return { type, summary: parts.filter(Boolean).join(" · ") };
}

/** Normalize one entry of the `images` array into a renderable descriptor. */
export function normalizeImageEntry(entry: unknown): ImageRef | null {
  if (entry == null) return null;

  if (typeof entry === "string") {
    const raw = entry.trim();
    if (!raw) return null;
    if (raw.toLowerCase() === "[object object]") {
      return { kind: "unresolved", raw, reason: "stringified object" };
    }
    const candidates = imageCandidates(raw);
    return candidates.length
      ? { kind: "image", candidates, alt: "", raw }
      : { kind: "unresolved", raw: truncate(raw), reason: "not mirrored" };
  }

  if (typeof entry === "object") {
    const o = entry as Record<string, unknown>;
    const url =
      typeof o.url === "string" ? o.url : typeof o.src === "string" ? o.src : "";
    const alt = typeof o.alt === "string" ? o.alt : "";
    // Diagram storage keys are private; only the problem detail route adds a presigned `url`.
    const contentKey = typeof o.content_key === "string" ? o.content_key : "";

    if (url.trim()) {
      const candidates = imageCandidates(url);
      if (!candidates.length) return { kind: "unresolved", raw: truncate(url), reason: "not mirrored" };
      // The presigned url is a credential, so the tooltip shows the key.
      return contentKey
        ? { kind: "image", candidates, alt, raw: truncate(contentKey), diagram: true }
        : { kind: "image", candidates, alt, raw: url };
    }

    if (contentKey) {
      return { kind: "diagram", raw: truncate(contentKey) };
    }

    // Thinkverse {"grid":12,"html":"<table>…"} — an HTML table, not an image.
    if (typeof o.html === "string" && o.html.trim()) {
      return { kind: "html", html: o.html, raw: truncate(JSON.stringify(o)) };
    }

    // Thinkverse declarative figure spec (graph / geometry / range / …).
    const spec = describeSpec(o);
    if (spec) {
      return {
        kind: "spec",
        specType: spec.type,
        summary: spec.summary,
        raw: truncate(JSON.stringify(o)),
      };
    }

    return {
      kind: "unresolved",
      raw: truncate(JSON.stringify(o)),
      reason: "object with no url or html",
    };
  }

  return {
    kind: "unresolved",
    raw: String(entry),
    reason: `unexpected ${typeof entry}`,
  };
}

/** Tolerates array | JSON-string | single object | bare string | null. */
export function normalizeImages(images: unknown): ImageRef[] {
  let value: unknown = images;

  if (typeof value === "string") {
    const s = value.trim();
    if (!s) return [];
    if (s.startsWith("[") || s.startsWith("{")) {
      try {
        value = JSON.parse(s);
      } catch {
        value = [s];
      }
    } else {
      value = [s];
    }
  }

  if (value && typeof value === "object" && !Array.isArray(value)) value = [value];
  if (!Array.isArray(value)) return [];

  const out: ImageRef[] = [];
  for (const entry of value) {
    const ref = normalizeImageEntry(entry);
    if (ref) out.push(ref);
  }
  return out;
}

/** Counts for the badge and the unresolved footer. */
export function imageRefSummary(images: unknown): {
  total: number;
  resolvable: number;
  unresolved: number;
  specs: number;
  diagrams: number;
} {
  const refs = normalizeImages(images);
  let unresolved = 0;
  let specs = 0;
  let diagrams = 0;
  // Specs and unloaded diagrams are neither a picture nor a failure.
  for (const r of refs) {
    if (r.kind === "unresolved") unresolved++;
    else if (r.kind === "spec") specs++;
    else if (r.kind === "diagram") diagrams++;
  }
  return {
    total: refs.length,
    resolvable: refs.length - unresolved - specs - diagrams,
    unresolved,
    specs,
    diagrams,
  };
}

/**
 * Back-compat shim for existing callers that want a single URL.
 * Prefer `imageCandidates` / `normalizeImages` — they expose failure.
 */
export function resolveImageUrl(src: string): string | null {
  return imageCandidates(src)[0] ?? null;
}
